import { LitElement, html, css, unsafeCSS } from 'lit';
import { fetchFixtures, fmtLocalDate, isLive } from './utils.js';
import { THEMES_CSS, SHARED_CSS } from './themes.js';

export class QghBase extends LitElement {
  static styles = [unsafeCSS(THEMES_CSS + SHARED_CSS)];

  static properties = {
    apiUrl:         { type: String,  attribute: 'api-url' },
    clientKey:      { type: String,  attribute: 'client-key' },
    refresh:        { type: Number },
    status:         { type: String },
    date:           { type: String },
    from:           { type: String },
    to:             { type: String },
    id:             { type: String },
    ids:            { type: String },
    league:         { type: String },
    season:         { type: String },
    timezone:       { type: String },
    nextDays:       { type: Number,  attribute: 'next-days' },
    pastDays:       { type: Number,  attribute: 'past-days' },
    noTodayRefresh: { type: Boolean, attribute: 'no-today-refresh' },
    _fixtures:      { state: true },
    _error:         { state: true },
    _loading:       { state: true },
  };

  constructor() {
    super();
    this._fixtures = [];
    this._error    = null;
    this._loading  = true;
    this.refresh   = 60;
    this.apiUrl    = 'https://api.quienganahoy.com';
  }

  connectedCallback() {
    super.connectedCallback();
    this._load();
    if (this.refresh > 0) {
      this._timer = setInterval(() => this._load(), this.refresh * 1000);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._timer) clearInterval(this._timer);
  }

  _params() {
    const p = {};
    ['date', 'from', 'to', 'id', 'ids', 'league', 'season', 'timezone']
      .forEach(k => { if (this[k]) p[k] = this[k]; });

    if (this.status) p.status = this.status.toUpperCase().replace(/,/g, '-');

    // Auto-detect browser timezone when not explicitly set
    if (!p.timezone) {
      p.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    }

    if ((this.nextDays > 0 || this.pastDays > 0) && !p.date && !p.from && !p.to) {
      const start = new Date();
      const end   = new Date();
      if (this.pastDays > 0) start.setDate(start.getDate() - this.pastDays);
      if (this.nextDays > 0) end.setDate(end.getDate() + this.nextDays);
      p.from = fmtLocalDate(start);
      p.to   = fmtLocalDate(end);
    } else if (!p.date && !p.from && !p.to) {
      p.date = fmtLocalDate();
    }
    return p;
  }

  async _load() {
    try {
      const p = this._params();
      let fixtures;

      const today = fmtLocalDate();
      const shouldSplit = !this.noTodayRefresh && p.from && p.to && p.from <= today && p.to >= today;

      if (shouldSplit) {
        const [todayData, rangeData] = await Promise.all([
          fetchFixtures(this.apiUrl, { ...p, from: undefined, to: undefined, date: today }, this.clientKey || ''),
          fetchFixtures(this.apiUrl, p, this.clientKey || ''),
        ]);
        const seen = new Set();
        fixtures = [...todayData, ...rangeData].filter(f => {
          if (seen.has(f.fixture.id)) return false;
          seen.add(f.fixture.id);
          return true;
        });
      } else {
        fixtures = await fetchFixtures(this.apiUrl, p, this.clientKey || '');
      }

      // Filtrar client-side usando fecha LOCAL (no UTC del ISO string)
      // Ej: partido jugado 22hs ART = 01hs UTC del día siguiente → el UTC date engaña al filtro
      if (p.from || p.to) {
        fixtures = fixtures.filter(f => {
          const d = f.fixture.date ? fmtLocalDate(new Date(f.fixture.date)) : null;
          if (!d) return true;
          if (p.from && d < p.from) return false;
          if (p.to   && d > p.to)   return false;
          return true;
        });
      }

      // Si la API devuelve un partido como NS/TBD pero ya pasó su horario (+ margen),
      // es un artefacto de caché del servidor — descartarlo.
      const now = Date.now();
      const STALE_NS_GRACE_MS = 30 * 60 * 1000; // 30 min de margen para demoras reales
      fixtures = fixtures.filter(f => {
        const s = f.fixture.status.short;
        if (s !== 'NS' && s !== 'TBD') return true;
        return new Date(f.fixture.date).getTime() >= now - STALE_NS_GRACE_MS;
      });

      const statusOrder = (f) => {
        const s = f.fixture.status.short;
        if (s === 'NS' || s === 'TBD') return 2; // próximos
        if (isLive(s))                 return 1; // en vivo
        return 0;                                 // finalizados primero
      };

      this._fixtures = fixtures.slice().sort((a, b) => {
        const ao = statusOrder(a);
        const bo = statusOrder(b);
        if (ao !== bo) return ao - bo;
        const da = new Date(a.fixture.date);
        const db = new Date(b.fixture.date);
        return da - db; // finalizados: más antiguo primero (más reciente justo antes de live); live/próximos: más temprano
      });
      this._error = null;
    } catch (e) {
      if (!this._fixtures.length) this._error = e.message;
    } finally {
      this._loading = false;
    }
  }

  _poweredBy() {
    const src = window.location.hostname;
    const href = `https://quienganahoy.com/?utm_source=${encodeURIComponent(src)}&utm_medium=widget&utm_campaign=powered_by`;
    return html`
      <div class="powered">
        <a href="${href}" target="_blank" rel="noopener">
          <span>Powered by</span>
          <img src="https://quienganahoy.com/svg/logo_qgh.svg" alt="QuienGanaHoy">
        </a>
      </div>`;
  }
}
